import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

const LIST_LABELS = {
  availableCols: 'Available',
  groupByCols: 'Group By',
  amountCols: 'Amounts',
};

export const PivotColsDialog = ({ availableCols, groupByCols, amountCols, closeDialog }) => {
  // One state object keyed by list id, so drag moves are a single generic splice between lists
  const [lists, setLists] = useState({
    availableCols: availableCols ?? [],
    groupByCols: groupByCols ?? [],
    amountCols: amountCols ?? [],
  });

  const submit = () => {
    closeDialog({
      groupByCols: lists.groupByCols,
      amountCols: lists.amountCols,
    });
  };

  // The column currently being dragged, so the amounts list can preview whether it accepts the drop
  const [draggingCol, setDraggingCol] = useState(null);

  const handleDragStart = ({ source }) => {
    setDraggingCol(lists[source.droppableId][source.index]);
  };

  const handleDragEnd = ({ source, destination }) => {
    setDraggingCol(null);

    // Dropped outside of any list
    if (!destination) {
      return;
    }

    const moved = lists[source.droppableId][source.index];

    // Only numeric columns can be summed, so reject a non-numeric column dropped into amounts.
    if (destination.droppableId === 'amountCols' && moved.type !== 'NUMBER') {
      return;
    }

    const newLists = {
      availableCols: Array.from(lists.availableCols),
      groupByCols: Array.from(lists.groupByCols),
      amountCols: Array.from(lists.amountCols),
    };

    newLists[source.droppableId].splice(source.index, 1);
    newLists[destination.droppableId].splice(destination.index, 0, moved);

    setLists(newLists);
  };

  const theme = useTheme();

  const renderDropColumn = (id) => {
    const cols = lists[id];

    // Preview that the amounts list only accepts numeric columns while dragging
    const disableDrop = id === 'amountCols' && draggingCol?.type !== 'NUMBER';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Typography align="center" variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>
          {LIST_LABELS[id]}
        </Typography>
        <Droppable droppableId={id} isDropDisabled={disableDrop}>
          {(provided, snapshot) => {
            const { isDraggingOver } = snapshot;

            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  bgcolor: theme.palette.surface.low,
                  borderRadius: 1,
                  p: 1,
                  border: 1,
                  borderColor: isDraggingOver ? alpha(theme.palette.surface.tint, 0.16) : 'transparent',
                  opacity: disableDrop ? 0.5 : 1,
                  cursor: disableDrop ? 'not-allowed' : 'default',
                  filter: disableDrop ? 'grayscale(0.6)' : 'none',
                  transition: 'background-color 0.2s, border-color 0.2s, opacity 0.2s',
                }}
              >
                {cols.map((col, index) => (
                  <Draggable key={col.field} draggableId={col.field} index={index}>
                    {(prov, snapshot) => (
                      <ButtonBase
                        component={Card}
                        focusRipple
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        sx={{
                          display: 'block',
                          width: '100%',
                          borderRadius: 1,
                          boxShadow: snapshot.isDragging ? 3 : 0,
                          mb: 1,
                          p: 1.5,
                          bgcolor:
                            snapshot.isDragging ?
                              alpha(theme.palette.surface.tint, 0.16)
                            : theme.palette.surface.container,
                          color: theme.palette.text.primary,
                          cursor: 'grab',
                          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.surface.tint, 0.08),
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            textAlign: 'center',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            overflowWrap: 'anywhere',
                            lineHeight: 1.2,
                          }}
                          title={col.displayName}
                        >
                          {col.displayName}
                        </Typography>
                      </ButtonBase>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            );
          }}
        </Droppable>
      </Box>
    );
  };

  return (
    <Dialog open fullWidth maxWidth="md">
      <DialogTitle>Configure Pivot</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              padding: 2,
              minHeight: 0,
            }}
          >
            {renderDropColumn('availableCols')}
            {renderDropColumn('groupByCols')}
            {renderDropColumn('amountCols')}
          </Box>
        </DragDropContext>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={submit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
